import React, { useEffect, useRef, useState } from 'react';
import TooltipPortal from './TooltipPortal';
import { translations } from '../../i18n/translations';
import { formatSezione } from '../../utils/cableLogic';

// Il numero è l'identificatore: il colore è solo un rinforzo per la scansione visiva,
// e resta volutamente lontano dai colori dei fili per non essere scambiato con essi.
const CHIP_PALETTE = [
  'bg-indigo-100 text-indigo-800 border-indigo-300',
  'bg-teal-100 text-teal-800 border-teal-300',
  'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  'bg-sky-100 text-sky-800 border-sky-300',
  'bg-lime-100 text-lime-800 border-lime-300',
  'bg-rose-100 text-rose-800 border-rose-300'
];

const OVER_CAPACITY_CLASSES = 'bg-amber-200 text-amber-900 border-amber-500';

const TerminalGroupChip = ({ groupIndex, group, lingua = 'it', useAWG = false, className = '' }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const chipRef = useRef(null);

  const lang = translations[lingua] ? lingua : 'it';
  const t = (key) => translations[lang]?.cableList?.[key] || translations.it.cableList[key] || key;

  const updatePosition = () => {
    if (!chipRef.current) return;
    const rect = chipRef.current.getBoundingClientRect();
    setPosition({ x: rect.left + (rect.width / 2), y: rect.top });
  };

  useEffect(() => {
    if (!group) return undefined;
    window.addEventListener('resize', updatePosition);
    updatePosition();
    return () => window.removeEventListener('resize', updatePosition);
  }, [group]);

  if (!group || groupIndex == null) return null;

  const label = groupIndex + 1;
  const chipClasses = group.isOverCapacity
    ? OVER_CAPACITY_CLASSES
    : CHIP_PALETTE[groupIndex % CHIP_PALETTE.length];

  return (
    <>
      <span
        ref={chipRef}
        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full border text-[10px] font-bold leading-none align-middle cursor-help ${chipClasses} ${className}`}
        onMouseEnter={() => {
          updatePosition();
          setShowTooltip(true);
        }}
        onMouseLeave={() => setShowTooltip(false)}
        aria-label={`${t('sharedTerminal')} ${label}`}
      >
        {label}
      </span>

      <TooltipPortal show={showTooltip}>
        <div
          className="fixed z-[9999] px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg max-w-xs pointer-events-none"
          style={{
            left: `${position.x}px`,
            top: `${position.y - 8}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="font-semibold mb-1 text-indigo-200">
            {t('sharedTerminal')} {label}
          </div>
          <div>{group.connection} · {group.terminal}</div>
          <div>{group.endpoints.length} {t('sharedTerminalConnections')}</div>
          <div className={group.isOverCapacity ? 'text-amber-300 font-semibold' : ''}>
            {t('sharedTerminalSection')} {formatSezione(group.sezioneTotale, useAWG)}
            {group.maxSezione != null && ` / ${formatSezione(group.maxSezione, useAWG)}`}
          </div>
        </div>
      </TooltipPortal>
    </>
  );
};

export default TerminalGroupChip;
